/**
 * Survey Detail Page - View survey details and manage responses
 * 
 * Connected to backend APIs:
 * - GET /api/v1/supplier-portal/surveys/:id
 * - GET /api/v1/supplier-portal/surveys/:id/responses
 * - POST /api/v1/supplier-portal/surveys/:id/responses/:responseId/approve
 * - POST /api/v1/supplier-portal/surveys/:id/responses/:responseId/reject
 * - GET /api/v1/supplier-portal/surveys/:id/aggregate
 */
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  ClipboardList,
  ChevronRight,
  Globe,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Users,
  Calendar,
  Eye,
  Mail,
  Rocket,
  XCircle,
  Check,
  X,
  Download,
  BarChart3,
  TrendingUp,
  Shield,
  FileText,
  ExternalLink,
  Copy,
  MessageSquare
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
import { apiFetch } from '../../../services/api_service';

export default function SurveyDetailPage() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [aggregateData, setAggregateData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingResponse, setProcessingResponse] = useState(null);

  useEffect(() => {
    if (surveyId) {
      fetchSurveyData();
    }
  }, [surveyId]);

  const fetchSurveyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [surveyData, responsesData] = await Promise.all([
        apiFetch(`/supplier-portal/surveys/${surveyId}`),
        apiFetch(`/supplier-portal/surveys/${surveyId}/responses`)
      ]);

      if (surveyData?._error) throw new Error(surveyData.message || 'Failed to fetch survey');
      setSurvey(surveyData);

      if (!responsesData?._error) {
        setResponses(responsesData.responses || []);
      }

      // Try to fetch aggregate data
      try {
        const aggData = await apiFetch(`/supplier-portal/surveys/${surveyId}/aggregate`);
        if (!aggData?._error) {
          setAggregateData(aggData);
        }
      } catch (e) {
        console.log('No aggregate data available');
      }
    } catch (err) {
      console.error('Error fetching survey:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveResponse = async (responseId) => {
    try {
      setProcessingResponse(responseId);
      const data = await apiFetch(
        `/supplier-portal/surveys/${surveyId}/responses/${responseId}/approve`,
        { method: 'POST', body: { comments: 'Approved' } }
      );
      if (data?._error) throw new Error(data.message || 'Failed to approve response');
      fetchSurveyData();
    } catch (err) {
      console.error('Error approving response:', err);
      alert(err.message);
    } finally {
      setProcessingResponse(null);
    }
  };

  const handleRejectResponse = async (responseId, reason) => {
    try {
      setProcessingResponse(responseId);
      const data = await apiFetch(
        `/supplier-portal/surveys/${surveyId}/responses/${responseId}/reject`,
        { method: 'POST', body: { reason: reason || 'Rejected' } }
      );
      if (data?._error) throw new Error(data.message || 'Failed to reject response');
      fetchSurveyData();
    } catch (err) {
      console.error('Error rejecting response:', err);
      alert(err.message);
    } finally {
      setProcessingResponse(null);
    }
  };

  const handleLaunchSurvey = async () => {
    try {
      const data = await apiFetch(`/supplier-portal/surveys/${surveyId}/launch`, { method: 'POST' });
      if (data?._error) throw new Error(data.message || 'Failed to launch survey');
      fetchSurveyData();
    } catch (err) {
      console.error('Error launching survey:', err);
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw size={32} className="text-purple-400 animate-spin" />
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle size={48} className="text-red-400" />
        <h2 className="text-xl font-semibold text-white">Survey not found</h2>
        <p className="text-[#6B6B6B]">{error || 'The survey you are looking for does not exist.'}</p>
        <button 
          onClick={() => navigate('/value-chain/supplier-portal/surveys')}
          className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30"
        >
          Back to Surveys
        </button>
      </div>
    );
  }

  // Calculate status
  let status = 'draft';
  if (survey.timeline?.closedAt) status = 'closed';
  else if (survey.timeline?.launchedAt) status = 'active';

  // Calculate progress
  const totalRecipients = survey.progress?.totalRecipients || 0;
  const submitted = survey.progress?.surveysSubmitted || 0;
  const approved = survey.progress?.surveysApproved || 0;
  const responseRate = totalRecipients > 0 ? Math.round((submitted / totalRecipients) * 100) : 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'responses', label: 'Responses', icon: Users, count: submitted },
    { id: 'questions', label: 'Questions', icon: ClipboardList },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  return (
    <div className="space-y-6" data-testid="survey-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[#6B6B6B] text-sm">
        <Link to="/value-chain/supplier-portal" className="hover:text-purple-400 flex items-center gap-1">
          <Globe size={14} />
          <span>Supplier Portal</span>
        </Link>
        <ChevronRight size={14} />
        <Link to="/value-chain/supplier-portal/surveys" className="hover:text-purple-400">
          Surveys
        </Link>
        <ChevronRight size={14} />
        <span className="text-white font-mono">{survey.surveyId}</span>
      </div>

      {/* Header */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2.5 py-1 rounded bg-[#1F1F1F] text-xs font-mono text-[#A0A0A0]">
                {survey.surveyId}
              </span>
              <StatusBadge status={status} />
              {survey.frameworks?.map((fw, idx) => (
                <span key={idx} className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 text-xs border border-blue-500/30">
                  {fw}
                </span>
              ))}
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">{survey.surveyName}</h1>
            {survey.purpose && (
              <p className="text-[#A0A0A0] text-sm">{survey.purpose}</p>
            )}

            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2 text-[#6B6B6B]">
                <Calendar size={14} />
                Created: {new Date(survey.timeline?.createdAt).toLocaleDateString()}
              </div>
              {survey.timeline?.dueDate && (
                <div className="flex items-center gap-2 text-[#6B6B6B]">
                  <Clock size={14} />
                  Due: {new Date(survey.timeline.dueDate).toLocaleDateString()}
                </div>
              )}
              {survey.timeline?.launchedAt && (
                <div className="flex items-center gap-2 text-emerald-400">
                  <Rocket size={14} />
                  Launched: {new Date(survey.timeline.launchedAt).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {status === 'draft' && (
              <button
                onClick={handleLaunchSurvey}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"
              >
                <Rocket size={16} />
                Launch Survey
              </button>
            )}
            <button className="flex items-center gap-2 px-4 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg hover:bg-[#2F2F2F] transition-colors">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* Progress Stats */}
        <div className="grid grid-cols-5 gap-4 mt-6 pt-6 border-t border-[#1F1F1F]">
          <ProgressStat label="Recipients" value={totalRecipients} icon={Users} />
          <ProgressStat label="Emails Sent" value={survey.progress?.emailsSent || 0} icon={Mail} />
          <ProgressStat label="Opened" value={survey.progress?.emailsOpened || 0} icon={Eye} />
          <ProgressStat label="Submitted" value={submitted} icon={CheckCircle} color="emerald" />
          <ProgressStat label="Approved" value={approved} icon={Shield} color="purple" />
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-[#6B6B6B]">Response Rate</span>
            <span className={`font-semibold ${
              responseRate >= 80 ? 'text-emerald-400' :
              responseRate >= 50 ? 'text-amber-400' :
              'text-red-400'
            }`}>{responseRate}%</span>
          </div>
          <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${
                responseRate >= 80 ? 'bg-emerald-500' :
                responseRate >= 50 ? 'bg-amber-500' :
                'bg-red-500'
              }`}
              style={{ width: `${responseRate}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-[#111111] border border-[#1F1F1F] rounded-xl p-1 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-purple-500/20 text-purple-400'
                : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.count !== undefined && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                activeTab === tab.id ? 'bg-purple-500/30' : 'bg-[#1F1F1F]'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab survey={survey} responses={responses} aggregateData={aggregateData} />
      )}

      {activeTab === 'responses' && (
        <ResponsesTab 
          responses={responses} 
          onApprove={handleApproveResponse}
          onReject={handleRejectResponse}
          processingResponse={processingResponse}
        />
      )}

      {activeTab === 'questions' && (
        <QuestionsTab questions={survey.configuration?.questions || []} />
      )}

      {activeTab === 'analytics' && (
        <AnalyticsTab aggregateData={aggregateData} responses={responses} />
      )}
    </div>
  );
}

// Sub-components
function StatusBadge({ status }) {
  const config = {
    draft: { label: 'Draft', bg: 'bg-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-400' },
    active: { label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400', dot: 'bg-emerald-400' },
    closed: { label: 'Closed', bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]', dot: 'bg-[#6B6B6B]' }
  }[status];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot} ${status === 'active' ? 'animate-pulse' : ''}`} />
      {config.label}
    </span>
  );
}

function ProgressStat({ label, value, icon: Icon, color }) {
  const colorClass = color ? `text-${color}-400` : 'text-[#A0A0A0]';
  
  return (
    <div className="text-center">
      <div className={`w-10 h-10 rounded-lg bg-[#1F1F1F] flex items-center justify-center mx-auto mb-2 ${colorClass}`}>
        <Icon size={18} />
      </div>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-xs text-[#6B6B6B]">{label}</div>
    </div>
  );
}

function OverviewTab({ survey, responses, aggregateData }) {
  const pendingResponses = responses.filter(r => r.status === 'submitted' && r.approval?.status === 'pending');

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Configuration */}
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <ClipboardList className="text-purple-400" size={18} />
            Survey Configuration
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <ConfigItem label="Require Evidence" value={survey.configuration?.requireEvidence ? 'Yes' : 'No'} />
            <ConfigItem label="Multiple Submissions" value={survey.configuration?.allowMultipleSubmissions ? 'Allowed' : 'Not Allowed'} />
            <ConfigItem label="Auto Reminders" value={survey.configuration?.autoReminders ? 'Enabled' : 'Disabled'} />
            <ConfigItem label="Reminder Frequency" value={`Every ${survey.configuration?.reminderFrequency || 7} days`} />
            <ConfigItem label="Questions" value={`${survey.configuration?.questions?.length || 0} questions`} />
            <ConfigItem label="Frameworks" value={survey.frameworks?.join(', ') || 'None'} />
          </div>
        </div>

        {/* Pending Approvals */}
        {pendingResponses.length > 0 && (
          <div className="bg-[#111111] border border-amber-500/30 rounded-xl p-6">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <AlertCircle className="text-amber-400" size={18} />
              Pending Approvals ({pendingResponses.length})
            </h3>
            <div className="space-y-3">
              {pendingResponses.slice(0, 3).map(response => (
                <div key={response.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-white">{response.supplierName}</p>
                    <p className="text-xs text-[#6B6B6B]">
                      Submitted: {new Date(response.submittedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      response.dataQualityAssessment?.overallScore >= 80 
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : response.dataQualityAssessment?.overallScore >= 50
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {response.dataQualityAssessment?.overallScore || 0}% Quality
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Aggregate Summary */}
        {aggregateData && aggregateData.responseCount > 0 && (
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-emerald-400" size={18} />
              Data Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-[#6B6B6B]">Responses</span>
                <span className="text-sm font-medium text-white">{aggregateData.responseCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#6B6B6B]">Coverage</span>
                <span className="text-sm font-medium text-white">
                  {Math.round((aggregateData.coverage?.byCount || 0) * 100)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Integration Status */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="text-purple-400" size={18} />
            Integration
          </h3>
          <div className="space-y-2 text-sm">
            {survey.integration?.obligationId && (
              <div className="flex items-center gap-2 text-[#A0A0A0]">
                <CheckCircle size={14} className="text-emerald-400" />
                Linked to Obligation
              </div>
            )}
            <div className="flex items-center gap-2 text-[#A0A0A0]">
              <FileText size={14} className={survey.integration?.evidenceCreated?.length > 0 ? 'text-emerald-400' : 'text-[#6B6B6B]'} />
              {survey.integration?.evidenceCreated?.length || 0} Evidence Created
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigItem({ label, value }) {
  return (
    <div className="p-3 bg-[#0A0A0A] rounded-lg">
      <p className="text-xs text-[#6B6B6B] mb-1">{label}</p>
      <p className="text-sm text-white">{value}</p>
    </div>
  );
}

function ResponsesTab({ responses, onApprove, onReject, processingResponse }) {
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredResponses = responses.filter(r => 
    filterStatus === 'all' || r.status === filterStatus
  );

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-[#6B6B6B]">Filter:</span>
        {['all', 'submitted', 'approved', 'rejected'].map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
              filterStatus === status
                ? 'bg-purple-500/20 text-purple-400'
                : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Responses Table */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0A0A0A] border-b border-[#1F1F1F]">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase">Supplier</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase">Status</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase">Quality</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase">Submitted</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-[#6B6B6B] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]">
            {filteredResponses.map(response => (
              <tr key={response.id} className="hover:bg-[#1F1F1F]/50">
                <td className="px-5 py-4">
                  <div>
                    <p className="text-sm font-medium text-white">{response.supplierName}</p>
                    <p className="text-xs text-[#6B6B6B]">{response.submittedBy?.contactEmail}</p>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <ResponseStatusBadge status={response.status} />
                </td>
                <td className="px-5 py-4">
                  <QualityScore score={response.dataQualityAssessment?.overallScore || 0} />
                </td>
                <td className="px-5 py-4">
                  <span className="text-sm text-[#A0A0A0]">
                    {response.submittedAt ? new Date(response.submittedAt).toLocaleDateString() : '-'}
                  </span>
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {response.status === 'submitted' && response.approval?.status === 'pending' && (
                      <>
                        <button
                          onClick={() => onApprove(response.id)}
                          disabled={processingResponse === response.id}
                          className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 disabled:opacity-50"
                          title="Approve"
                        >
                          {processingResponse === response.id ? (
                            <RefreshCw size={14} className="animate-spin" />
                          ) : (
                            <Check size={14} />
                          )}
                        </button>
                        <button
                          onClick={() => onReject(response.id, 'Data quality insufficient')}
                          disabled={processingResponse === response.id}
                          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50"
                          title="Reject"
                        >
                          <X size={14} />
                        </button>
                      </>
                    )}
                    <button className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B]" title="View">
                      <Eye size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredResponses.length === 0 && (
          <div className="p-12 text-center">
            <Users className="mx-auto text-[#2F2F2F] mb-4" size={48} />
            <p className="text-[#6B6B6B]">No responses yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ResponseStatusBadge({ status }) {
  const config = {
    submitted: { label: 'Pending', bg: 'bg-amber-500/20', text: 'text-amber-400' },
    approved: { label: 'Approved', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
    rejected: { label: 'Rejected', bg: 'bg-red-500/20', text: 'text-red-400' },
    draft: { label: 'Draft', bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]' }
  }[status] || { label: status, bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]' };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

function QualityScore({ score }) {
  const color = score >= 80 ? 'emerald' : score >= 50 ? 'amber' : 'red';
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
        <div 
          className={`h-full bg-${color}-500`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={`text-xs font-medium text-${color}-400`}>{score}%</span>
    </div>
  );
}

function QuestionsTab({ questions }) {
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
      <h3 className="text-base font-semibold text-white mb-6">Survey Questions ({questions.length})</h3>
      <div className="space-y-4">
        {questions.map((q, idx) => (
          <div key={q.questionId || idx} className="p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F]">
            <div className="flex items-start gap-4">
              <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">
                {idx + 1}
              </span>
              <div className="flex-1">
                <p className="text-sm text-white font-medium mb-2">{q.questionText}</p>
                <div className="flex items-center gap-3 text-xs text-[#6B6B6B]">
                  <span className="px-2 py-0.5 rounded bg-[#1F1F1F]">{q.questionType}</span>
                  {q.required && <span className="text-amber-400">Required</span>}
                  {q.unit && <span>Unit: {q.unit}</span>}
                </div>
                {q.helpText && (
                  <p className="text-xs text-[#6B6B6B] mt-2">{q.helpText}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsTab({ aggregateData, responses }) {
  if (!aggregateData || !aggregateData.aggregated) {
    return (
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-12 text-center">
        <BarChart3 className="mx-auto text-[#2F2F2F] mb-4" size={48} />
        <h3 className="text-lg font-semibold text-white mb-2">No Data Yet</h3>
        <p className="text-sm text-[#6B6B6B]">Analytics will be available once responses are approved.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <p className="text-xs text-[#6B6B6B] mb-1">Responses</p>
          <p className="text-2xl font-bold text-white">{aggregateData.responseCount}</p>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <p className="text-xs text-[#6B6B6B] mb-1">Coverage (Count)</p>
          <p className="text-2xl font-bold text-white">{Math.round((aggregateData.coverage?.byCount || 0) * 100)}%</p>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <p className="text-xs text-[#6B6B6B] mb-1">Coverage (Spend)</p>
          <p className="text-2xl font-bold text-white">{Math.round((aggregateData.coverage?.bySpend || 0) * 100)}%</p>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <p className="text-xs text-[#6B6B6B] mb-1">Avg Quality</p>
          <p className="text-2xl font-bold text-white">
            {responses.length > 0 
              ? Math.round(responses.reduce((sum, r) => sum + (r.dataQualityAssessment?.overallScore || 0), 0) / responses.length)
              : 0}%
          </p>
        </div>
      </div>

      {/* Aggregated Data */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        <h3 className="text-base font-semibold text-white mb-4">Aggregated Responses</h3>
        <div className="space-y-4">
          {Object.entries(aggregateData.aggregated).map(([key, data]) => (
            <div key={key} className="p-4 bg-[#0A0A0A] rounded-lg">
              <p className="text-sm text-white font-medium mb-3">{data.questionText}</p>
              <div className="grid grid-cols-5 gap-4 text-sm">
                <div>
                  <p className="text-xs text-[#6B6B6B]">Total</p>
                  <p className="text-white font-medium">{data.total?.toLocaleString()} {data.unit}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Average</p>
                  <p className="text-white font-medium">{data.average?.toLocaleString()} {data.unit}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Min</p>
                  <p className="text-white font-medium">{data.min?.toLocaleString()} {data.unit}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Max</p>
                  <p className="text-white font-medium">{data.max?.toLocaleString()} {data.unit}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">Responses</p>
                  <p className="text-white font-medium">{data.count}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
